import { useEffect, useState } from 'react'
import { ShieldCheck, KeyRound, MailCheck } from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import { sendEmailVerification } from 'firebase/auth'
import { auth } from '../lib/firebase'
import {
  hasTotpFactor,
  startTotpEnroll,
  completeTotpEnroll,
  unenrollTotp,
  type TotpEnrollStart,
} from '../lib/totpMfa'
import { useI18n } from '../i18n/useI18n'
import { showToast } from './Toast'

/**
 * 店舗オーナー向け 2段階認証 (TOTP) の **任意 opt-in** 管理 (#840).
 *
 * 顧客側 (#815) と同じ Firebase TOTP 基盤 (`lib/totpMfa`) を再利用。顧客は
 * enrollment gate で強制だが、店舗オーナーは登録フローを変えない方針のため
 * 本コンポーネントは設定画面 (StorePreferences) からの自発的な有効化/無効化
 * のみを提供する (強制しない / キャンセル可能)。
 *
 * enroll 済オーナーの次回ログインは Firebase が `auth/multi-factor-auth-required`
 * を投げ、StoreLogin の TotpSignInModal が解決する。端末紛失時の lockout は
 * self-service 復旧を作らず運営が Admin SDK で unenroll する方針 (#789 と同じ /
 * docs/infra-manual-operations.md §17)。
 */
/**
 * `required`: #2663 TOTP が必須 (STORE_TOTP_MODE=required_all)。無効化の導線を出さず、文言を「必須」にする。
 * `onEnrolled`: 登録できたときに呼ぶ (必須化の gate が「ログインし直す」に切り替えるため)。
 */
export default function StoreTotpManager({ required = false, onEnrolled }: { required?: boolean; onEnrolled?: () => void } = {}) {
  const { t } = useI18n()
  const [enrolled, setEnrolled] = useState(() => hasTotpFactor(auth.currentUser))
  const [mode, setMode] = useState<'idle' | 'enrolling'>('idle')
  const [confirmingDisable, setConfirmingDisable] = useState(false)
  const [start, setStart] = useState<TotpEnrollStart | null>(null)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  // Firebase Identity Platform は MFA enroll に emailVerified=true を要求する
  // (#846 / lib/totpMfa.ts 冒頭コメント参照)。メール+パスワード登録経路で
  // 確認リンク未クリックの間は enroll 開始時点で `auth/unverified-email` が
  // throw されるため、UI 側で先回りして判定し誤誘導を避ける。
  const [emailVerified, setEmailVerified] = useState(
    () => auth.currentUser?.emailVerified ?? true,
  )

  // mount 時に user.reload() で最新の emailVerified を取り直す: ユーザーが
  // メール内リンクをクリック直後にアプリへ戻ってきたケースで、stale な
  // emailVerified=false が表示されてしまうのを避ける。失敗は無視 (auth
  // emulator が未起動など local edge case でも UI を壊さない)。
  useEffect(() => {
    const user = auth.currentUser
    if (!user) return
    user.reload()
      .then(() => setEmailVerified(user.emailVerified))
      .catch((e) => console.warn('[StoreTotp] reload failed:', e))
  }, [])

  const resendVerification = async () => {
    const user = auth.currentUser
    if (!user || busy) return
    setBusy(true)
    try {
      await sendEmailVerification(user)
      showToast({
        type: 'success',
        message: t('storeSettings.totpVerificationEmailSent'),
      })
    } catch (e) {
      console.error('[StoreTotp] resend verification failed:', e)
      showToast({
        type: 'error',
        message: t('storeSettings.totpVerificationEmailFailed'),
      })
    } finally {
      setBusy(false)
    }
  }

  const refreshVerified = async () => {
    const user = auth.currentUser
    if (!user || busy) return
    setBusy(true)
    try {
      await user.reload()
      setEmailVerified(user.emailVerified)
      if (!user.emailVerified) {
        showToast({
          type: 'info',
          message: t('storeSettings.totpStillUnverified'),
        })
      }
    } catch (e) {
      console.warn('[StoreTotp] manual reload failed:', e)
    } finally {
      setBusy(false)
    }
  }

  const beginEnroll = async () => {
    const user = auth.currentUser
    if (!user || busy) return
    // 念のため再確認 (reload 完了前のクリックでも誤発火を防ぐ)
    if (!user.emailVerified) {
      setEmailVerified(false)
      return
    }
    setBusy(true)
    setMode('enrolling')
    try {
      setStart(await startTotpEnroll(user))
    } catch (e) {
      console.error('[StoreTotp] start failed:', e)
      // Firebase 側で emailVerified が反転したケース (例: 別端末で解除) を
      // 救済: auth/unverified-email を catch して gate UI に戻す。
      const code = (e as { code?: string })?.code
      if (code === 'auth/unverified-email') {
        setEmailVerified(false)
      } else {
        showToast({ type: 'error', message: t('storeSettings.totpInitError') })
      }
      setMode('idle')
    } finally {
      setBusy(false)
    }
  }

  const verify = async () => {
    const user = auth.currentUser
    if (!user || !start || busy) return
    setBusy(true)
    try {
      await completeTotpEnroll(user, start.secret, code.trim())
      setEnrolled(true)
      setMode('idle')
      setStart(null)
      setCode('')
      showToast({ type: 'success', message: t('totp.enrolled') })
      onEnrolled?.()
    } catch (e) {
      const c = (e as { code?: string })?.code
      console.error('[StoreTotp] verify failed:', e)
      showToast({
        type: 'error',
        message:
          c === 'auth/invalid-verification-code' || c === 'auth/invalid-code'
            ? t('totp.invalidCode')
            : t('totp.enrollFailed'),
      })
    } finally {
      setBusy(false)
    }
  }

  const cancelEnroll = () => {
    setMode('idle')
    setStart(null)
    setCode('')
  }

  const disable = async () => {
    const user = auth.currentUser
    if (!user || busy) return
    setBusy(true)
    try {
      await unenrollTotp(user)
      setEnrolled(false)
      setConfirmingDisable(false)
      showToast({ type: 'success', message: t('totp.enrolled') })
    } catch (e) {
      console.error('[StoreTotp] unenroll failed:', e)
      showToast({ type: 'error', message: t('totp.enrollFailed') })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="bg-white rounded-2xl shadow-card p-5">
      <div className="flex items-start gap-3">
        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-yokoso-red/10 shrink-0">
          <ShieldCheck size={20} className="text-yokoso-red" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-display text-sm font-semibold text-text-primary">
              {t('storeSettings.totpTitle')}
            </h2>
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-body font-semibold ${
                enrolled ? 'bg-matcha/10 text-matcha' : 'bg-gray-100 text-text-tertiary'
              }`}
            >
              {enrolled
                ? t('storeSettings.totpStatusOn')
                : t('storeSettings.totpStatusOff')}
            </span>
          </div>
          <p className="font-body text-xs text-text-secondary mt-1 leading-relaxed">
            {t(required ? 'storeSettings.totpHelpRequired' : 'storeSettings.totpHelp')}
          </p>
        </div>
      </div>

      {/* 有効時: 無効化導線 (必須のときは出さない。無効化しても次のログインで登録に戻されるだけ) */}
      {enrolled && mode === 'idle' && (
        <div className="mt-4">
          <p className="font-body text-xs text-matcha leading-relaxed mb-3">
            {t('storeSettings.totpEnabledNote')}
          </p>
          {required ? null : confirmingDisable ? (
            <div className="rounded-xl border border-yokoso-red/40 bg-yokoso-red/5 p-3">
              <p className="font-body text-sm text-text-primary mb-3">
                {t('storeSettings.totpDisableConfirmQ')}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={disable}
                  disabled={busy}
                  className="flex-1 h-11 rounded-xl bg-yokoso-red text-white font-display font-bold text-sm tap-effect disabled:opacity-50"
                >
                  {t('storeSettings.totpConfirmYes')}
                </button>
                <button
                  onClick={() => setConfirmingDisable(false)}
                  disabled={busy}
                  className="flex-1 h-11 rounded-xl border border-gray-200 font-body text-sm text-text-secondary disabled:opacity-50"
                >
                  {t('storeSettings.totpCancel')}
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setConfirmingDisable(true)}
              className="h-11 px-4 rounded-xl border border-gray-200 font-body text-sm text-text-secondary tap-effect"
            >
              {t('storeSettings.totpDisable')}
            </button>
          )}
        </div>
      )}

      {/* 未設定: emailVerified=false なら enroll をブロックして再送 CTA を出す
          (Firebase MFA 仕様、#846)。verified なら通常の enroll ボタン。 */}
      {!enrolled && mode === 'idle' && !emailVerified && (
        <div className="mt-4 rounded-xl border border-gold/40 bg-gold/5 p-4">
          <div className="flex items-start gap-2 mb-3">
            <MailCheck size={18} className="text-gold shrink-0 mt-0.5" />
            <p className="font-body text-xs text-text-primary leading-relaxed">
              {t('storeSettings.totpRequiresVerifiedEmail')}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={resendVerification}
              disabled={busy}
              className="flex-1 h-11 rounded-xl bg-yokoso-red text-white font-display font-bold text-sm tap-effect disabled:opacity-50"
            >
              {t('storeSettings.totpResendVerification')}
            </button>
            <button
              onClick={refreshVerified}
              disabled={busy}
              className="flex-1 h-11 rounded-xl border border-gray-200 font-body text-sm text-text-secondary disabled:opacity-50"
            >
              {t('storeSettings.totpRefreshVerified')}
            </button>
          </div>
        </div>
      )}

      {!enrolled && mode === 'idle' && emailVerified && (
        <button
          onClick={beginEnroll}
          disabled={busy}
          className="mt-4 h-12 w-full rounded-xl bg-yokoso-red text-white font-display font-bold text-sm tap-effect disabled:opacity-50"
        >
          {t('storeSettings.totpEnable')}
        </button>
      )}

      {/* enroll パネル (QR + 手入力キー + 6桁コード) */}
      {mode === 'enrolling' && (
        <div className="mt-4 border-t border-gray-100 pt-4">
          {!start ? (
            <p className="text-sm font-body text-text-secondary text-center py-6">…</p>
          ) : (
            <>
              <p className="font-body text-xs text-text-secondary mb-3 leading-relaxed">
                {t('storeSettings.totpScanInstruction')}
              </p>
              <div className="flex justify-center mb-3">
                <div className="p-3 bg-white border border-gray-200 rounded-lg">
                  <QRCodeSVG value={start.qrCodeUrl} size={160} />
                </div>
              </div>
              <div className="mb-4">
                <span className="block text-xs font-body text-text-secondary mb-1">
                  {t('totp.manualKeyLabel')}
                </span>
                <code className="block text-xs font-mono break-all bg-gray-50 border border-gray-200 rounded-lg p-2 text-text-primary select-all">
                  {start.manualKey}
                </code>
              </div>
              <label className="block text-xs font-body text-text-secondary mb-1">
                {t('totp.codeInputLabel')}
              </label>
              <input
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                className="w-full h-12 mb-4 text-center tracking-[0.4em] text-lg font-mono border border-gray-300 rounded-xl focus:outline-none focus:border-yokoso-red"
                placeholder="000000"
              />
              <button
                onClick={verify}
                disabled={busy || code.length !== 6}
                className={`w-full h-12 rounded-xl font-display font-bold text-sm transition-colors ${
                  busy || code.length !== 6
                    ? 'bg-gray-300 text-white cursor-not-allowed'
                    : 'bg-yokoso-red text-white tap-effect'
                }`}
              >
                {busy ? t('totp.enrolling') : t('totp.verifyAction')}
              </button>
              <button
                onClick={cancelEnroll}
                disabled={busy}
                className="w-full h-11 mt-2 rounded-xl font-body text-sm text-text-secondary disabled:opacity-50"
              >
                {t('storeSettings.totpCancel')}
              </button>
            </>
          )}
          <p className="flex items-start gap-2 font-body text-[11px] text-text-secondary mt-3 leading-relaxed">
            <KeyRound size={13} className="text-text-tertiary shrink-0 mt-0.5" />
            {t('totp.recoveryContact')}
          </p>
        </div>
      )}
    </div>
  )
}
